import { datadogRum } from '@datadog/browser-rum'

export type MedicationRefillSurface =
  | 'refill_task'
  | 'visit_note_block'
  | 'visit_note'
  | 'medications_page'
  | 'medications_card'
  | 'encounter_meds'

export type MedicationRefillDraftFailureReason =
  | 'no_elation_patient'
  | 'missing_medication_id'
  | 'not_elation_sourced'
  | 'missing_thread'
  | 'missing_physician'
  | 'missing_qty_units'
  | 'request_failed'

interface MedicationRefillActionFields {
  surface?: MedicationRefillSurface
  medicationRequestId?: string
}

// Datadog query: `@action.name:"medication_refill.draft_requested"`
export function reportMedicationRefillDraftRequested(
  fields: MedicationRefillActionFields & { orderType: 'Refill' }
): void {
  datadogRum.addAction('medication_refill.draft_requested', fields)
}

// Datadog query: `@action.name:"medication_refill.draft_created"`
export function reportMedicationRefillDraftCreated(
  fields: MedicationRefillActionFields & { draftId: number }
): void {
  datadogRum.addAction('medication_refill.draft_created', fields)
}

// Datadog query: `@action.name:"medication_refill.draft_failed"`
export function reportMedicationRefillDraftFailed(
  fields: MedicationRefillActionFields & {
    reason: MedicationRefillDraftFailureReason
  }
): void {
  datadogRum.addAction('medication_refill.draft_failed', fields)
}

// Datadog query: `@action.name:"medication_refill.sign_opened"`
export function reportMedicationRefillSignOpened(
  fields: MedicationRefillActionFields & { draftId: number | string }
): void {
  datadogRum.addAction('medication_refill.sign_opened', fields)
}

export function classifyCreateDraftFailureReason(
  errorMessage: string
): MedicationRefillDraftFailureReason {
  const normalized = errorMessage.toLowerCase()
  if (
    normalized.includes('missing medication thread') ||
    normalized.includes('thread.id')
  ) {
    return 'missing_thread'
  }
  if (
    normalized.includes('missing medication identifier') ||
    normalized.includes('medication.id')
  ) {
    return 'missing_medication_id'
  }
  if (
    normalized.includes('prescribing_physician') ||
    normalized.includes('prescribing physician')
  ) {
    return 'missing_physician'
  }
  if (normalized.includes('qty_units')) {
    return 'missing_qty_units'
  }
  return 'request_failed'
}

const CREATE_DRAFT_FAILURE_MESSAGES: Record<
  MedicationRefillDraftFailureReason,
  string | undefined
> = {
  missing_thread: 'Medication thread is missing in Elation',
  missing_medication_id: 'Medication identifier is missing in Elation',
  missing_physician: 'Prescribing physician is missing for this medication',
  missing_qty_units:
    'Quantity units are missing for this medication in Elation',
  no_elation_patient: undefined,
  not_elation_sourced: undefined,
  request_failed: undefined,
}

export function humanizeCreateDraftErrorMessage(errorMessage: string): string {
  return (
    CREATE_DRAFT_FAILURE_MESSAGES[
      classifyCreateDraftFailureReason(errorMessage)
    ] ?? errorMessage
  )
}
